import React, { useState, useEffect } from 'react';
import { Store, ChevronDown } from 'lucide-react';
import StoreSwitcherModal from './StoreSwitcherModal';
import { authService } from '../api/authService';
import { configService } from '../api/configService';

export default function StoreSelector({ onSwitch }) {
  const [showModal, setShowModal] = useState(false);
  const [tenantName, setTenantName] = useState('Pilih Toko');
  const [tenantAddress, setTenantAddress] = useState(
    () => localStorage.getItem('tenantAddress') || ''
  );
  const [tenantLogoUrl, setTenantLogoUrl] = useState(
    () => localStorage.getItem('tenantLogoUrl') || ''
  );
  const [logoFailed, setLogoFailed] = useState(false);

  // Load tenant info on mount and when localStorage / tenantUpdated event changes
  useEffect(() => {
    let cancelled = false;

    const loadTenantInfoFromStorage = () => {
      let name = localStorage.getItem('tenantName');
      if (!name) {
        const userData = authService.getCurrentUser();
        if (userData?.tenant_name) {
          name = userData.tenant_name;
          localStorage.setItem('tenantName', name);
        }
      }
      setTenantName(name || 'Pilih Toko');
      setTenantAddress(localStorage.getItem('tenantAddress') || '');
      setTenantLogoUrl(localStorage.getItem('tenantLogoUrl') || '');
      setLogoFailed(false);
    };

    // Load from storage immediately
    loadTenantInfoFromStorage();

    // Fetch latest store data from API to ensure name, address, and logoUrl are always up to date
    configService
      .getStore()
      .then((res) => {
        const store = res?.data ?? res ?? {};
        const storeName = store?.name || '';
        const address = store?.settings?.address || store?.address || '';
        const logoUrl = store?.logo_url || store?.settings?.logo_url || '';
        if (cancelled) return;

        if (storeName) {
          localStorage.setItem('tenantName', storeName);
          setTenantName(storeName);
        }
        if (address) {
          localStorage.setItem('tenantAddress', address);
          setTenantAddress(address);
        } else {
          localStorage.removeItem('tenantAddress');
          setTenantAddress('');
        }
        if (logoUrl) {
          localStorage.setItem('tenantLogoUrl', logoUrl);
          setTenantLogoUrl(logoUrl);
          setLogoFailed(false);
        } else {
          localStorage.removeItem('tenantLogoUrl');
          setTenantLogoUrl('');
        }
      })
      .catch(() => {});

    // Listen for storage changes & custom tenantUpdated events
    const handleStorageChange = () => {
      loadTenantInfoFromStorage();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('tenantUpdated', handleStorageChange);
    return () => {
      cancelled = true;
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('tenantUpdated', handleStorageChange);
    };
  }, []);

  const handleSwitch = (tenant) => {
    // Update local state immediately
    setTenantName(tenant.name || 'Pilih Toko');
    // Hapus cache agar di-fetch ulang untuk tenant baru.
    localStorage.removeItem('tenantAddress');
    localStorage.removeItem('tenantLogoUrl');
    setTenantAddress('');
    setTenantLogoUrl('');
    setLogoFailed(false);

    // Notify parent so it can refetch/reload data
    onSwitch?.(tenant);
  };

  const showLogoImage = tenantLogoUrl && !logoFailed;

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        title="Klik untuk mengganti toko"
        className="group flex items-center gap-2.5 px-2 py-1.5 -ml-2 rounded-xl hover:bg-slate-100/80 transition-colors w-full min-w-0 max-w-full text-left"
      >
        {showLogoImage ? (
          <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center shrink-0 ring-1 ring-slate-200 overflow-hidden shadow-sm">
            <img
              src={tenantLogoUrl}
              alt={tenantName}
              className="w-full h-full object-cover"
              onError={() => setLogoFailed(true)}
            />
          </div>
        ) : (
          <div className="w-9 h-9 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center shrink-0 ring-1 ring-teal-600/20 shadow-sm shadow-teal-600/20">
            <Store className="w-4 h-4 text-white" />
          </div>
        )}
        <div className="text-left min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <h2 className="text-sm font-semibold text-slate-900 truncate">
              {tenantName}
            </h2>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400 group-hover:text-teal-600 transition-colors flex-shrink-0" />
          </div>
          {tenantAddress && (
            <p className="text-[11px] text-slate-500 leading-tight truncate text-left">
              {tenantAddress}
            </p>
          )}
        </div>
      </button>

      {showModal && (
        <StoreSwitcherModal
          onClose={() => setShowModal(false)}
          onSwitch={handleSwitch}
        />
      )}
    </>
  );
}
